import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Toolbar } from '@mui/material';
import NavBar from '../components/NavBar';
import DailyInsightDialog from '../components/insights/DailyInsightDialog';
import ViewAsBanner from '../components/insights/ViewAsBanner';
import InsightUndoSnackbar from '../components/insights/InsightUndoSnackbar';
import api from '../adapters/api';

export default function AppLayout({ children }) {
  const spacerRef = useRef(null);
  const bannerRef = useRef(null);
  const { pathname } = useLocation();

  // บันทึกการเปิดหน้า (baseline ของระบบ insight) — ไม่สนใจผลลัพธ์ ไม่กระทบการใช้งาน
  useEffect(() => {
    api.post('/api/insight/usage', { path: pathname }).catch(() => {});
  }, [pathname]);

  useEffect(() => {
    const el = spacerRef.current;
    const banner = bannerRef.current;
    if (!el) return;
    // keep --nav-h in sync with the actual rendered AppBar height at all breakpoints
    // --nav-h รวมแถบ "ดูในฐานะผู้ใช้" ด้วย (ถ้ามี) เพื่อให้หน้าที่สูง 100vh - --nav-h พอดีจอ ไม่เกิด scrollbar
    // --appbar-h = ความสูง AppBar อย่างเดียว (ใช้วางตัวแถบเอง)
    // เขียนค่าในเฟรมถัดไป และเฉพาะเมื่อค่าเปลี่ยนจริง — ถ้าเขียนทันทีใน callback หน้าจะจัด layout ใหม่ในเฟรมเดียวกัน
    // แล้วเบราว์เซอร์แจ้ง "ResizeObserver loop completed with undelivered notifications" (เกิดตอนย่อ/ขยายหน้าจอ)
    let frame = 0;
    let last = { appbar: '', nav: '' };
    const sync = () => {
      const appbar = `${el.offsetHeight}px`;
      const nav = `${el.offsetHeight + (banner?.offsetHeight || 0)}px`;
      if (appbar === last.appbar && nav === last.nav) return;
      last = { appbar, nav };
      const root = document.documentElement.style;
      root.setProperty('--appbar-h', appbar);
      root.setProperty('--nav-h', nav);
    };
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(sync);
    };
    sync();
    const ro = new ResizeObserver(schedule);
    ro.observe(el);
    if (banner) ro.observe(banner);
    return () => { cancelAnimationFrame(frame); ro.disconnect(); };
  }, []);

  return (
    <>
      <NavBar />
      <DailyInsightDialog />
      <InsightUndoSnackbar />
      {/* invisible spacer — same height as the fixed AppBar; also drives --nav-h */}
      <Toolbar ref={spacerRef} aria-hidden="true" />
      <Box ref={bannerRef} sx={{ position: 'sticky', top: 'var(--appbar-h, 64px)', zIndex: 1090 }}>
        <ViewAsBanner />
      </Box>
      <Box
        component="main"
        sx={{
          paddingLeft: 'env(safe-area-inset-left, 0px)',
          paddingRight: 'env(safe-area-inset-right, 0px)',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        }}
      >
        {children}
      </Box>
    </>
  );
}
